import { useEffect, useState } from "react";
import { Plus, RefreshCcw, Trash } from "lucide-react";
import LoadingScreen from "../../Utils/LoadingScreen";
import useVolunteerBankDetails from "../../../Hooks/useVolunteerBankDetails";
import useVolunteersAllApi from "../../../Hooks/Volunteer Api/useVoluntersAllApi";

export default function VolunteersBank() {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  const {
    insertVolunteerBankDetails,
    getVolunteerBankDetails,
    bankDetails,
    loading,
    deleteVolunteerBankDetails,
  } = useVolunteerBankDetails();

  const { volunteer, fetchAllVolunteers } = useVolunteersAllApi();

  const itemsPerPage = 15;

  const [form, setForm] = useState({
    volunteerId: "",
    email: "",
    phone: "",
    bankName: "",
    bankHolderName: "",
    accountNumber: "",
    ifscCode: "",
    aadharNumber: "",
    panNumber: "",
    primaryAccount: "Y",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleVolunteerSelect = (e) => {
    const selectedCode = e.target.value;

    const selectedVolunteer = volunteer.find(
      (v) => v.volunteer_code === selectedCode,
    );

    if (selectedVolunteer) {
      setForm((prev) => ({
        ...prev,
        volunteerId: selectedCode,
        email: selectedVolunteer.email || "",
        phone: selectedVolunteer.phone_number || "",
      }));
    }
  };

  const resetForm = () => {
    setForm({
      volunteerId: "",
      email: "",
      phone: "",
      bankName: "",
      bankHolderName: "",
      accountNumber: "",
      ifscCode: "",
      aadharNumber: "",
      panNumber: "",
      primaryAccount: "Y",
    });
  };

  const handleDeleteClick = (bank) => {
    setSelectedBank(bank);
    setDeleteOpen(true);
  };

  useEffect(() => {
    fetchAllVolunteers();
    getVolunteerBankDetails("");
  }, []);

  const filtered = bankDetails.filter(
    (bank) =>
      bank.bankName?.toLowerCase().includes(search.toLowerCase()) ||
      bank.bankHolderName?.toLowerCase().includes(search.toLowerCase()) ||
      bank.accountNumber?.includes(search) ||
      bank.ifscCode?.toLowerCase().includes(search.toLowerCase()) ||
      bank.panNumber?.toLowerCase().includes(search.toLowerCase()) ||
      bank.aadhaarNumber?.includes(search),
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const isFormValid = () => {
    return (
      form.volunteerId.trim() !== "" &&
      form.email.trim() !== "" &&
      form.phone.trim() !== "" &&
      form.bankName.trim() !== "" &&
      form.bankHolderName.trim() !== "" &&
      form.accountNumber.trim() !== "" &&
      form.ifscCode.trim() !== "" &&
      form.aadharNumber.trim() !== "" &&
      form.panNumber.trim() !== ""
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">Volunteer Bank Accounts</h1>
      </div>

      <div className="mb-4 flex justify-between items-center space-y-3 sm:space-y-0 sm:flex-row flex-col">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by bank name or holder..."
          className="w-full md:w-1/3 px-3 py-1 border rounded shadow-sm"
        />

        <div className="flex space-x-4">
          <button
            onClick={() => setOpen(true)}
            className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-1 rounded shadow"
          >
            <Plus size={16} /> <span>Add Bank Details</span>
          </button>

          <button
            onClick={async () => {
              await getVolunteerBankDetails("");
            }}
            className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-400 text-white px-4 py-1 rounded shadow"
            disabled={loading}
          >
            <RefreshCcw size={16} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {/* Add bank details model */}
          {open && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50 p-3 z-50">
              <div
                className="bg-white rounded-2xl shadow-sm w-full max-w-4xl 
                    h-[95vh] md:h-[85vh] 
                    flex flex-col overflow-hidden"
              >
                {/* HEADER */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-500 px-6 md:px-8 py-4 rounded-t-2xl shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-white/20 p-2 rounded-lg">
                        <Plus className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl font-bold text-white">
                          Add Volunteer Bank Details
                        </h2>
                        <p className="text-blue-100 text-xs md:text-sm">
                          Fill in the information below to add new bank account
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setOpen(false);
                        resetForm();
                      }}
                      className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-all flex justify-center h-10 w-10 bg-white/20"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* SCROLLABLE BODY */}
                <div className="flex-1 overflow-y-auto p-4 md:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Volunteer Information */}
                    <div className="space-y-4">
                      <div className="border-l-4 border-blue-500 pl-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Volunteer Information
                        </h3>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Volunteer{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="volunteerId"
                            value={form.volunteerId}
                            onChange={handleVolunteerSelect}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select a volunteer</option>
                            {volunteer?.map((v) => (
                              <option
                                key={v.volunteer_code}
                                value={v.volunteer_code}
                              >
                                {v.first_name} {v.last_name} ({v.volunteer_code}
                                )
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="volunteer@example.com"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={form.phone}
                            onChange={handleChange}
                            placeholder="9876543210"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Aadhar Number{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="aadharNumber"
                            value={form.aadharNumber}
                            onChange={handleChange}
                            placeholder="123456789012"
                            maxLength="12"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Bank Information */}
                    <div className="space-y-4">
                      <div className="border-l-4 border-green-500 pl-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                          Bank Information
                        </h3>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bank Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="bankName"
                            value={form.bankName}
                            onChange={handleChange}
                            placeholder="State Bank of India"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Holder Name{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="bankHolderName"
                            value={form.bankHolderName}
                            onChange={handleChange}
                            placeholder="Shivam Kumar"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Account Number{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="accountNumber"
                            value={form.accountNumber}
                            onChange={handleChange}
                            placeholder="1234567890123456"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            IFSC Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="ifscCode"
                            value={form.ifscCode}
                            onChange={handleChange}
                            placeholder="SBIN0001234"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PAN Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="panNumber"
                          value={form.panNumber}
                          onChange={handleChange}
                          placeholder="ABCDE1234F"
                          maxLength="10"
                          style={{ textTransform: "uppercase" }}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* FOOTER */}
                <div className="px-4 md:px-8 py-4 border-t flex flex-col sm:flex-row justify-end gap-3 shrink-0">
                  <button
                    onClick={() => {
                      setOpen(false);
                      resetForm();
                    }}
                    className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg w-full sm:w-auto"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      const success = await insertVolunteerBankDetails(form);
                      if (success) {
                        setOpen(false);
                        await getVolunteerBankDetails("");
                        resetForm();
                      }
                    }}
                    disabled={loading || !isFormValid()}
                    className={`px-6 py-3 rounded-lg font-medium transition-all w-full sm:w-auto ${loading || !isFormValid()
                      ? "bg-gray-400 opacity-50 cursor-not-allowed"
                      : "bg-gradient-to-r from-orange-500 to-orange-500 hover:from-orange-600 hover:to-orange-600 text-white"
                      }`}
                  >
                    Save Bank Details
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* delete model */}
          {deleteOpen && selectedBank && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
              <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-lg">
                <h2 className="text-xl font-semibold text-red-600 mb-4">
                  Delete Bank Details
                </h2>

                <p className="mb-4 text-gray-700">
                  Are you sure you want to delete this bank detail?
                </p>

                <div className="bg-gray-100 p-4 rounded mb-4 text-sm">
                  <p>
                    <b>Bank:</b> {selectedBank.bankName}
                  </p>
                  <p>
                    <b>Holder:</b> {selectedBank.bankHolderName}
                  </p>
                  <p>
                    <b>Account:</b> {selectedBank.accountNumber}
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setDeleteOpen(false);
                      setSelectedBank(null);
                    }}
                    className="px-4 py-2 bg-gray-400 text-white rounded"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      const success = await deleteVolunteerBankDetails(
                        selectedBank.id,
                        form.volunteerId,
                      );

                      if (success) {
                        setDeleteOpen(false);
                        setSelectedBank(null);
                        await getVolunteerBankDetails("");
                      }
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto border rounded-md shadow-sm">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-blue-400 text-sm font-semibold text-white">
                  <th className="px-4 py-2">ID</th>
                  <th className="px-4 py-2">Bank Name</th>
                  <th className="px-4 py-2">Holder Name</th>
                  <th className="px-4 py-2">Account Number</th>
                  <th className="px-4 py-2">IFSC Code</th>
                  <th className="px-4 py-2">Aadhar</th>
                  <th className="px-4 py-2">PAN</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentData.map((bank) => (
                  <tr key={bank.id} className="text-sm border-t">
                    <td className="px-4 py-2">{bank.id}</td>
                    <td className="px-4 py-2">{bank.bankName}</td>
                    <td className="px-4 py-2">{bank.bankHolderName}</td>
                    <td className="px-4 py-2">{bank.accountNumber}</td>
                    <td className="px-4 py-2">{bank.ifscCode}</td>
                    <td className="px-4 py-2">{bank.aadhaarNumber}</td>
                    <td className="px-4 py-2">{bank.panNumber}</td>
                    <td className="px-4 py-2">{bank.email}</td>
                    <td className="px-4 py-2">{bank.phone}</td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => {
                          handleDeleteClick(bank);
                        }}
                        className="text-red-600"
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
